import React, { useState } from "react";

const FreeTrialInfo: React.FC = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    facilityAddress: "",
    city: "",
    state: "",
    phoneNumber: "",
    imageKey: "",
    map: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [showOrgImageHelp, setShowOrgImageHelp] = useState(false);
  const [showMapImageHelp, setShowMapImageHelp] = useState(false);

  const handleFileChange =
    (field: "imageKey" | "map") =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) {
          setForm((prev) => ({ ...prev, [field]: "" }));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (typeof result === "string") {
            // strip data URL prefix, keep only base64 content
            const base64 = result.split(",")[1] || result;
            setForm((prev) => ({ ...prev, [field]: base64 }));
          }
        };
        reader.readAsDataURL(file);
      };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Invalid email format";

    if (!form.facilityAddress.trim())
      newErrors.facilityAddress = "Facility address is required";
    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    if (!form.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        "https://apis.roomrounds.com/api/organization/saveOrganizationFreeTrial",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", accept: "text/plain" },
          body: JSON.stringify({
            organizationName: form.name,
            organizationEmail: form.email,
            location: form.facilityAddress,
            city: form.city,
            state: form.state,
            phoneNumber: form.phoneNumber,
            imageKey: form.imageKey,
            map: form.map,
          }),
        }
      );

      let result: {
        succeeded?: boolean;
        message?: string;
        httpStatusCode?: number;
      } | null = null;

      try {
        result = await response.json();
      } catch {
        result = null;
      }

      const succeeded =
        result?.succeeded === true &&
        (result.httpStatusCode == null || result.httpStatusCode < 400);

      if (response.ok && succeeded) {
        setSubmitted(true);
        setApiMessage(
          "Thank you! Go to mail box to get your free trial link."
        );
        setForm({
          name: "",
          email: "",
          facilityAddress: "",
          city: "",
          state: "",
          phoneNumber: "",
          imageKey: "",
          map: "",
        });
      } else {
        setSubmitted(false);
        setApiMessage(
          result?.message ||
            "Failed to submit free trial request. Please try again."
        );
      }
    } catch (error) {
      setSubmitted(false);
      setApiMessage("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-xl mx-auto py-12 px-4">
      <h2 className="text-3xl font-bold mb-6 text-center">
        Start Your Free Trial
      </h2>

      {!submitted && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your  Name"

              className="w-full border rounded px-3 py-2"
            />

          </div>
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your Organization Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              name="facilityAddress"
              placeholder="Address"
              value={form.facilityAddress}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.facilityAddress && (
              <p className="text-red-600 text-sm mt-1">
                {errors.facilityAddress}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
              {errors.city && (
                <p className="text-red-600 text-sm mt-1">{errors.city}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
              {errors.state && (
                <p className="text-red-600 text-sm mt-1">{errors.state}</p>
              )}
            </div>
          </div>

          <div>
            <input
              type="tel"
              name="phoneNumber"
              placeholder="Your Phone Number"
              value={form.phoneNumber}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 15),
                }))
              }
              className="w-full border rounded px-3 py-2"
            />
            {errors.phoneNumber && (
              <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>
            )}
          </div>

          {/* <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <span>Organization Image</span>
              <button
                type="button"
                onClick={() => setShowOrgImageHelp((prev) => !prev)}
                className="w-5 h-5 rounded-full border border-gray-400 text-xs flex items-center justify-center text-gray-600 hover:bg-gray-100"
                aria-label="Organization image help"
              >
                ?
              </button>
            </label>
            {showOrgImageHelp && (
              <p className="text-xs text-gray-500 mb-2">
                Download your facility logo as an image file (PNG or JPG) and upload it here.
              </p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange("imageKey")}
              className="w-full"
            />
            {form.imageKey && (
              <p className="text-xs text-green-700 mt-1">
                Image selected and converted to base64.
              </p>
            )}
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <span>Map Image</span>
              <button
                type="button"
                onClick={() => setShowMapImageHelp((prev) => !prev)}
                className="w-5 h-5 rounded-full border border-gray-400 text-xs flex items-center justify-center text-gray-600 hover:bg-gray-100"
                aria-label="Map image help"
              >
                ?
              </button>
            </label>
            {showMapImageHelp && (
              <p className="text-xs text-gray-500 mb-2">
                Upload your fire evacuation map or similar floor plan.
              </p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange("map")}
              className="w-full"
            />
            {form.map && (
              <p className="text-xs text-green-700 mt-1">
                Map image selected and converted to base64.
              </p>
            )}
          </div> */}
          <div>
            <textarea
              name="message"
              placeholder="Your  Message"
              className="w-full border rounded px-3 py-2"
              rows={4}
            />

          </div>
          <button
            type="submit"
            disabled={loading}
            className={`bg-blue-600 text-white px-4 py-2 rounded w-full ${loading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
          >
            {loading ? "Sending..." : "Request Free Trial"}
          </button>
        </form>
      )}

      {apiMessage && (
        <div
          className={`relative p-4 pr-10 rounded text-center mt-4 ${
            submitted
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {apiMessage}
          <button
            type="button"
            onClick={() => setApiMessage(null)}
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded hover:bg-black/10 text-lg leading-none"
            aria-label="Close message"
          >
            ×
          </button>
        </div>
      )}

      <div className="mt-8 text-center text-gray-600">
        <div>
          Email:{" "}
          <a
            href="mailto:sales@Roomrounds.com"
            className="text-blue-600 hover:underline"
          >
            sales@Roomrounds.com
          </a>
        </div>
        {/* <div>
          Phone:{" "}
          <a href="tel:+1234567890" className="text-blue-600 hover:underline">
            +1 (234) 567-890
          </a>
        </div> */}
      </div>
    </section>
  );
};

export default FreeTrialInfo;
